import React, { useState, useEffect } from 'react';
import { Phone, PhoneCall, PhoneOff, PhoneMissed, Voicemail, Mic, Settings, Users, Plus, X, ChevronDown, Volume2 } from 'lucide-react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';

export default function PhoneSystemTab({ allUsers, contacts, canCreate, canUpdate, onContactClick }) {
  const [activeTab, setActiveTab] = useState('callLog');
  const [kpis, setKpis] = useState({ totalCalls: 0, avgDuration: 0, inbound: 0, outbound: 0, missed: 0 });
  const [callRecords, setCallRecords] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [voicemailDrops, setVoicemailDrops] = useState([]);
  const [ivrMenus, setIvrMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCall, setExpandedCall] = useState(null);
  const [showAddNumber, setShowAddNumber] = useState(false);
  const [showAddVoicemail, setShowAddVoicemail] = useState(false);
  const [showAddIVR, setShowAddIVR] = useState(false);
  const [filters, setFilters] = useState({ direction: 'all', dateRange: '7d', disposition: 'all' });
  const [editingNumber, setEditingNumber] = useState(null);
  const [editingIVR, setEditingIVR] = useState(null);

  useEffect(() => {
    loadAllData();
  }, [filters]);

  const loadAllData = async () => {
    setLoading(true);
    await safeAsync(async () => {
      const [calls, numbers, voicemails, ivrs] = await Promise.all([
        window.zignagenixAPI.getCRMCallRecords(filters),
        window.zignagenixAPI.getCRMPhoneNumbers(),
        window.zignagenixAPI.getCRMVoicemailDrops(),
        window.zignagenixAPI.getCRMIVRMenus(),
      ]);

      setCallRecords(calls || []);
      setPhoneNumbers(numbers || []);
      setVoicemailDrops(voicemails || []);
      setIvrMenus(ivrs || []);

      // Calculate KPIs
      const inbound = calls?.filter(c => c.direction === 'inbound').length || 0;
      const outbound = calls?.filter(c => c.direction === 'outbound').length || 0;
      const missed = calls?.filter(c => c.status === 'missed').length || 0;
      const avgDur = calls?.length ? Math.round(calls.reduce((sum, c) => sum + (c.duration || 0), 0) / calls.length) : 0;
      setKpis({ totalCalls: calls?.length || 0, avgDuration: avgDur, inbound, outbound, missed });
    });
    setLoading(false);
  };

  const handleAddNumber = async (data) => {
    await safeAsync(async () => {
      await window.zignagenixAPI.addCRMPhoneNumber(data);
      setShowAddNumber(false);
      await loadAllData();
    });
  };

  const handleUpdateNumber = async (id, data) => {
    await safeAsync(async () => {
      await window.zignagenixAPI.updateCRMPhoneNumber(id, data);
      setEditingNumber(null);
      await loadAllData();
    });
  };

  const handleAddVoicemail = async (data) => {
    await safeAsync(async () => {
      await window.zignagenixAPI.createCRMVoicemailDrop(data);
      setShowAddVoicemail(false);
      await loadAllData();
    });
  };

  const handleDeleteVoicemail = async (id) => {
    if (window.confirm('Delete this voicemail drop?')) {
      await safeAsync(async () => {
        await window.zignagenixAPI.deleteCRMVoicemailDrop(id);
        await loadAllData();
      });
    }
  };

  const handleAddIVR = async (data) => {
    await safeAsync(async () => {
      await window.zignagenixAPI.createCRMIVRMenu(data);
      setShowAddIVR(false);
      await loadAllData();
    });
  };

  const handleUpdateIVR = async (id, data) => {
    await safeAsync(async () => {
      await window.zignagenixAPI.updateCRMIVRMenu(id, data);
      setEditingIVR(null);
      await loadAllData();
    });
  };

  const handleExpandCall = async (callId) => {
    if (expandedCall?.id === callId) {
      setExpandedCall(null);
      return;
    }
    await safeAsync(async () => {
      const detail = await window.zignagenixAPI.getCRMCallDetail(callId);
      setExpandedCall(detail);
    });
  };

  return (
    <div className="p-6 space-y-6" style={{ background: '#f5f5f5' }}>
      {/* KPI Row */}
      <div className="grid grid-cols-5 gap-4">
        <KPI icon={PhoneCall} label="Total Calls (7d)" value={kpis.totalCalls} trend="+5%" />
        <KPI icon={Phone} label="Avg Duration" value={`${kpis.avgDuration}s`} trend="↓" />
        <KPI icon={PhoneCall} label="Inbound" value={kpis.inbound} color="bg-blue-100" />
        <KPI icon={PhoneOff} label="Outbound" value={kpis.outbound} color="bg-green-100" />
        <KPI icon={PhoneMissed} label="Missed" value={kpis.missed} color="bg-red-100" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-300">
        {[
          { id: 'callLog', label: 'Call Log', icon: PhoneCall },
          { id: 'numbers', label: 'Phone Numbers', icon: Phone },
          { id: 'voicemail', label: 'Voicemail Drops', icon: Voicemail },
          { id: 'ivr', label: 'IVR Menus', icon: Mic },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition ${
              activeTab === tab.id
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Call Log Tab */}
      {activeTab === 'callLog' && <CallLogTab filters={filters} setFilters={setFilters} callRecords={callRecords} expandedCall={expandedCall} onExpandCall={handleExpandCall} onContactClick={onContactClick} />}

      {/* Phone Numbers Tab */}
      {activeTab === 'numbers' && (
        <div className="space-y-4">
          {canCreate && (
            <button onClick={() => setShowAddNumber(true)} className={btnPrimary} style={{ width: 'fit-content' }}>
              <Plus size={18} /> Add Phone Number
            </button>
          )}
          <div className="grid grid-cols-2 gap-4">
            {phoneNumbers.length === 0 ? (
              <div className={emptyMsg}>No phone numbers configured.</div>
            ) : (
              phoneNumbers.map(num => (
                <PhoneNumberCard
                  key={num.id}
                  number={num}
                  allUsers={allUsers}
                  canUpdate={canUpdate}
                  onEdit={() => setEditingNumber(num)}
                  onUpdate={(data) => handleUpdateNumber(num.id, data)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Voicemail Drops Tab */}
      {activeTab === 'voicemail' && (
        <div className="space-y-4">
          {canCreate && (
            <button onClick={() => setShowAddVoicemail(true)} className={btnPrimary} style={{ width: 'fit-content' }}>
              <Plus size={18} /> New Voicemail Drop
            </button>
          )}
          <div className="space-y-3">
            {voicemailDrops.length === 0 ? (
              <div className={emptyMsg}>No voicemail drops. Create one to get started.</div>
            ) : (
              voicemailDrops.map(vm => (
                <div key={vm.id} style={cardWrap} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 flex-1">
                    <Volume2 size={20} className="text-gray-600" />
                    <div>
                      <div className="font-semibold text-gray-900">{vm.name}</div>
                      <div className="text-sm text-gray-500">{vm.duration}s • Used {vm.useCount || 0} times</div>
                    </div>
                  </div>
                  {canUpdate && (
                    <button onClick={() => handleDeleteVoicemail(vm.id)} className={btnDanger} style={{ width: 'fit-content' }}>
                      <X size={16} /> Delete
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* IVR Menus Tab */}
      {activeTab === 'ivr' && (
        <div className="space-y-4">
          {canCreate && (
            <button onClick={() => setShowAddIVR(true)} className={btnPrimary} style={{ width: 'fit-content' }}>
              <Plus size={18} /> New IVR Menu
            </button>
          )}
          <div className="space-y-3">
            {ivrMenus.length === 0 ? (
              <div className={emptyMsg}>No IVR menus configured.</div>
            ) : (
              ivrMenus.map(ivr => (
                <IVRMenuCard
                  key={ivr.id}
                  menu={ivr}
                  canUpdate={canUpdate}
                  onEdit={() => setEditingIVR(ivr)}
                  voicemailDrops={voicemailDrops}
                  phoneNumbers={phoneNumbers}
                  allUsers={allUsers}
                  onUpdate={(data) => handleUpdateIVR(ivr.id, data)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddNumber && <AddPhoneNumberModal onClose={() => setShowAddNumber(false)} onSave={handleAddNumber} allUsers={allUsers} />}
      {editingNumber && <EditPhoneNumberModal number={editingNumber} onClose={() => setEditingNumber(null)} onSave={(data) => handleUpdateNumber(editingNumber.id, data)} allUsers={allUsers} />}
      {showAddVoicemail && <AddVoicemailModal onClose={() => setShowAddVoicemail(false)} onSave={handleAddVoicemail} />}
      {showAddIVR && <AddIVRModal onClose={() => setShowAddIVR(false)} onSave={handleAddIVR} voicemailDrops={voicemailDrops} phoneNumbers={phoneNumbers} allUsers={allUsers} />}
      {editingIVR && <EditIVRModal menu={editingIVR} onClose={() => setEditingIVR(null)} onSave={(data) => handleUpdateIVR(editingIVR.id, data)} voicemailDrops={voicemailDrops} phoneNumbers={phoneNumbers} allUsers={allUsers} />}
    </div>
  );
}

// Call Log Sub-Tab
function CallLogTab({ filters, setFilters, callRecords, expandedCall, onExpandCall, onContactClick }) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-4 gap-3" style={cardWrap}>
        <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} style={inp}>
          <option value="all">All Directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <select value={filters.dateRange} onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })} style={inp}>
          <option value="24h">Last 24h</option>
          <option value="7d">Last 7d</option>
          <option value="30d">Last 30d</option>
        </select>
        <select value={filters.disposition} onChange={(e) => setFilters({ ...filters, disposition: e.target.value })} style={inp}>
          <option value="all">All Dispositions</option>
          <option value="answered">Answered</option>
          <option value="missed">Missed</option>
          <option value="voicemail">Voicemail</option>
        </select>
        <input type="text" placeholder="Search contact..." style={inp} />
      </div>

      {/* Call Table */}
      <div style={cardWrap}>
        {callRecords.length === 0 ? (
          <div style={{ padding: '2rem' }} className={emptyMsg}>No calls found.</div>
        ) : (
          <div className="responsive-scroll overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 720 }}>
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-700">Date/Time</th>
                  <th className="text-left p-3 font-semibold text-gray-700">From</th>
                  <th className="text-left p-3 font-semibold text-gray-700">To</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Direction</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Duration</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Disposition</th>
                  <th className="text-center p-3 font-semibold text-gray-700">Recording</th>
                </tr>
              </thead>
              <tbody>
                {callRecords.map(call => (
                  <React.Fragment key={call.id}>
                    <tr onClick={() => onExpandCall(call.id)} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                      <td className="p-3 text-gray-900">{call.timestamp}</td>
                      <td className="p-3 text-gray-600">{call.from}</td>
                      <td className="p-3 text-gray-600">{call.to}</td>
                      <td className="p-3">
                        <Badge text={call.direction} color={call.direction === 'inbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'} />
                      </td>
                      <td className="p-3 text-gray-600">{call.duration}s</td>
                      <td className="p-3">
                        <Badge text={call.status} color={call.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'} />
                      </td>
                      <td className="p-3 text-gray-600">{call.disposition}</td>
                      <td className="p-3 text-center">{call.recording && <Mic size={16} className="mx-auto text-blue-600" />}</td>
                    </tr>
                    {expandedCall?.id === call.id && (
                      <tr className="bg-blue-50">
                        <td colSpan="8" className="p-4">
                          <CallDetail call={expandedCall} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CallDetail({ call }) {
  return (
    <div className="space-y-3">
      <div>
        <strong>Transcription:</strong>
        <p className="text-sm text-gray-700 mt-1">{call.transcription || 'No transcription available.'}</p>
      </div>
      <div>
        <strong>AI Summary:</strong>
        <p className="text-sm text-gray-700 mt-1">{call.aiSummary || 'Generating summary...'}</p>
      </div>
      {call.notes && (
        <div>
          <strong>Notes:</strong>
          <p className="text-sm text-gray-700 mt-1">{call.notes}</p>
        </div>
      )}
    </div>
  );
}

// Phone Number Card
function PhoneNumberCard({ number, allUsers, canUpdate, onEdit, onUpdate }) {
  const [settings, setSettings] = useState(number.settings || {});

  return (
    <div style={cardWrap} className="p-4 space-y-3">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-lg font-bold text-gray-900">{number.number}</div>
          <div className="text-sm text-gray-600">{number.friendlyName}</div>
        </div>
        <Badge text={number.type} color="bg-blue-100 text-blue-700" />
      </div>

      <div className="space-y-2 text-sm">
        <div>
          <strong>Assigned to:</strong> {allUsers.find(u => u.id === number.assignedUserId)?.name || 'Unassigned'}
        </div>
        <div className="flex gap-2">
          {number.capabilities?.voice && <Badge text="Voice" color="bg-gray-100 text-gray-700" />}
          {number.capabilities?.sms && <Badge text="SMS" color="bg-gray-100 text-gray-700" />}
          {number.capabilities?.mms && <Badge text="MMS" color="bg-gray-100 text-gray-700" />}
        </div>
      </div>

      {canUpdate && (
        <div className="space-y-2 border-t border-gray-200 pt-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.voicemailEnabled} onChange={(e) => setSettings({ ...settings, voicemailEnabled: e.target.checked })} />
            Voicemail Enabled
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.recordingEnabled} onChange={(e) => setSettings({ ...settings, recordingEnabled: e.target.checked })} />
            Recording Enabled
          </label>
          <button onClick={() => onUpdate(settings)} className={btnPrimary} style={{ width: '100%' }}>
            Save Settings
          </button>
        </div>
      )}
    </div>
  );
}

// IVR Menu Card
function IVRMenuCard({ menu, canUpdate, onEdit, voicemailDrops, phoneNumbers, allUsers, onUpdate }) {
  return (
    <div style={cardWrap} className="p-4 space-y-3">
      <div className="flex justify-between items-start">
        <div>
          <div className="font-bold text-gray-900">{menu.name}</div>
          <div className="text-sm text-gray-600">Greeting: {menu.greeting?.substring(0, 50)}...</div>
        </div>
        {canUpdate && <button onClick={onEdit} className={btnSm}>Edit</button>}
      </div>

      <div className="space-y-2">
        {menu.options?.map((opt, idx) => (
          <div key={idx} className="text-sm text-gray-700 pl-3 border-l-2 border-blue-300">
            <strong>Press {opt.digit}:</strong> {opt.action} {opt.target}
          </div>
        ))}
      </div>
    </div>
  );
}

// Modals
function AddPhoneNumberModal({ onClose, onSave, allUsers }) {
  const [data, setData] = useState({ number: '', friendlyName: '', type: 'mobile', assignedUserId: '' });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Add Phone Number</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <input type="text" placeholder="Phone Number" value={data.number} onChange={(e) => setData({ ...data, number: e.target.value })} style={inp} />
          <input type="text" placeholder="Friendly Name" value={data.friendlyName} onChange={(e) => setData({ ...data, friendlyName: e.target.value })} style={inp} />
          <select value={data.type} onChange={(e) => setData({ ...data, type: e.target.value })} style={inp}>
            <option value="mobile">Mobile</option>
            <option value="fixed">Fixed Line</option>
            <option value="toll-free">Toll Free</option>
          </select>
          <select value={data.assignedUserId} onChange={(e) => setData({ ...data, assignedUserId: e.target.value })} style={inp}>
            <option value="">Unassigned</option>
            {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button onClick={() => onSave(data)} className={btnPrimary} style={{ width: '100%' }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function EditPhoneNumberModal({ number, onClose, onSave, allUsers }) {
  const [data, setData] = useState(number);

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Edit Phone Number</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <input type="text" placeholder="Friendly Name" value={data.friendlyName} onChange={(e) => setData({ ...data, friendlyName: e.target.value })} style={inp} />
          <select value={data.assignedUserId || ''} onChange={(e) => setData({ ...data, assignedUserId: e.target.value })} style={inp}>
            <option value="">Unassigned</option>
            {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button onClick={() => onSave(data)} className={btnPrimary} style={{ width: '100%' }}>Update</button>
        </div>
      </div>
    </div>
  );
}

function AddVoicemailModal({ onClose, onSave }) {
  const [data, setData] = useState({ name: '', duration: 0, audioUrl: '', description: '' });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>New Voicemail Drop</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <input type="text" placeholder="Name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} style={inp} />
          <input type="text" placeholder="Audio URL" value={data.audioUrl} onChange={(e) => setData({ ...data, audioUrl: e.target.value })} style={inp} />
          <input type="number" placeholder="Duration (seconds)" value={data.duration} onChange={(e) => setData({ ...data, duration: parseInt(e.target.value) })} style={inp} />
          <textarea placeholder="Description" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} style={inp} rows="3" />
          <button onClick={() => onSave(data)} className={btnPrimary} style={{ width: '100%' }}>Create</button>
        </div>
      </div>
    </div>
  );
}

function AddIVRModal({ onClose, onSave, voicemailDrops, phoneNumbers, allUsers }) {
  const [data, setData] = useState({ name: '', greeting: '', options: [] });
  const [newOption, setNewOption] = useState({ digit: '', action: 'forward-number', target: '' });

  const addOption = () => {
    setData({ ...data, options: [...data.options, newOption] });
    setNewOption({ digit: '', action: 'forward-number', target: '' });
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>New IVR Menu</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
          <input type="text" placeholder="Name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} style={inp} />
          <textarea placeholder="Greeting Message" value={data.greeting} onChange={(e) => setData({ ...data, greeting: e.target.value })} style={inp} rows="3" />

          <div className="border-t pt-3">
            <label className="block text-sm font-semibold mb-2">Menu Options</label>
            <div className="space-y-2 mb-3">
              {data.options.map((opt, idx) => (
                <div key={idx} className="text-xs bg-gray-100 p-2 rounded">Press {opt.digit}: {opt.action} → {opt.target}</div>
              ))}
            </div>

            <div className="space-y-2">
              <input type="text" placeholder="Digit (0-9)" value={newOption.digit} onChange={(e) => setNewOption({ ...newOption, digit: e.target.value })} style={inp} maxLength="1" />
              <select value={newOption.action} onChange={(e) => setNewOption({ ...newOption, action: e.target.value })} style={inp}>
                <option value="forward-number">Forward to Number</option>
                <option value="forward-user">Forward to User</option>
                <option value="voicemail">Voicemail</option>
              </select>
              <select value={newOption.target} onChange={(e) => setNewOption({ ...newOption, target: e.target.value })} style={inp}>
                <option value="">Select target...</option>
                {newOption.action === 'forward-number' && phoneNumbers.map(p => <option key={p.id} value={p.number}>{p.number}</option>)}
                {newOption.action === 'forward-user' && allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                {newOption.action === 'voicemail' && voicemailDrops.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <button onClick={addOption} className={btnSm} style={{ width: '100%' }}>+ Add Option</button>
            </div>
          </div>

          <button onClick={() => onSave(data)} className={btnPrimary} style={{ width: '100%' }}>Create IVR Menu</button>
        </div>
      </div>
    </div>
  );
}

function EditIVRModal({ menu, onClose, onSave, voicemailDrops, phoneNumbers, allUsers }) {
  const [data, setData] = useState(menu);
  const [newOption, setNewOption] = useState({ digit: '', action: 'forward-number', target: '' });

  const addOption = () => {
    setData({ ...data, options: [...data.options, newOption] });
    setNewOption({ digit: '', action: 'forward-number', target: '' });
  };

  const removeOption = (idx) => {
    setData({ ...data, options: data.options.filter((_, i) => i !== idx) });
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Edit IVR Menu</h2>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
          <input type="text" placeholder="Name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} style={inp} />
          <textarea placeholder="Greeting Message" value={data.greeting} onChange={(e) => setData({ ...data, greeting: e.target.value })} style={inp} rows="3" />

          <div className="border-t pt-3">
            <label className="block text-sm font-semibold mb-2">Menu Options</label>
            <div className="space-y-2 mb-3">
              {data.options.map((opt, idx) => (
                <div key={idx} className="text-xs bg-gray-100 p-2 rounded flex justify-between items-center">
                  <span>Press {opt.digit}: {opt.action} → {opt.target}</span>
                  <button onClick={() => removeOption(idx)} className="text-red-600 hover:text-red-700"><X size={14} /></button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <input type="text" placeholder="Digit (0-9)" value={newOption.digit} onChange={(e) => setNewOption({ ...newOption, digit: e.target.value })} style={inp} maxLength="1" />
              <select value={newOption.action} onChange={(e) => setNewOption({ ...newOption, action: e.target.value })} style={inp}>
                <option value="forward-number">Forward to Number</option>
                <option value="forward-user">Forward to User</option>
                <option value="voicemail">Voicemail</option>
              </select>
              <select value={newOption.target} onChange={(e) => setNewOption({ ...newOption, target: e.target.value })} style={inp}>
                <option value="">Select target...</option>
                {newOption.action === 'forward-number' && phoneNumbers.map(p => <option key={p.id} value={p.number}>{p.number}</option>)}
                {newOption.action === 'forward-user' && allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                {newOption.action === 'voicemail' && voicemailDrops.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <button onClick={addOption} className={btnSm} style={{ width: '100%' }}>+ Add Option</button>
            </div>
          </div>

          <button onClick={() => onSave(data)} className={btnPrimary} style={{ width: '100%' }}>Update IVR Menu</button>
        </div>
      </div>
    </div>
  );
}
